import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mui/material';
import { createTenantSchemaVersion, getTenantSchemaVersions } from '../api/tenantSchemaApi';
import { DataTable } from '../components/DataTable';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { PageShell } from '../components/PageShell';
import { StatusBadge } from '../components/StatusBadge';

export function TenantSchemaVersionsPage() {
  const { t } = useTranslation(['tenantSchema', 'common']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTenantSchemaVersions({ per_page: 100 });
      setRows(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createDraft = async () => {
    const version = `v${Date.now()}`;
    await createTenantSchemaVersion({ version, name: t('tenantSchema:versions.draft_name', { version }) });
    load();
  };

  const columns = useMemo(() => [
    { id: 'version', label: t('tenantSchema:versions.columns.version') },
    { id: 'name', label: t('common:name') },
    { id: 'status', label: t('common:status'), render: (row) => <StatusBadge status={row.status} category="schema_version" /> },
    { id: 'migration_count', label: t('tenantSchema:versions.columns.migrations') },
    { id: 'artifacts_count', label: t('tenantSchema:versions.columns.artifacts') },
  ], [t]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={t('tenantSchema:versions.load_failed')} onRetry={load} />;

  return (
    <PageShell title={t('tenantSchema:versions.title')} subtitle={t('tenantSchema:versions.subtitle')}>
      <Stack direction="row" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={createDraft}>{t('tenantSchema:actions.create_version')}</Button>
      </Stack>
      <DataTable columns={columns} rows={rows} rowKey="id" emptyMessage={t('tenantSchema:versions.no_versions')} />
    </PageShell>
  );
}
